import { mapText } from "./map-i18n.js";
import {
  bookmarkCoordinatesFromSelection,
  buildBookmarkPanelStateBundle,
  createBookmarkFromSelection,
  normalizeBookmarks,
} from "./map-bookmark-state.js";
import {
  copyTextToClipboard,
  serializeBookmarkForCopy,
} from "./map-bookmark-io.js";
import { dispatchBookmarksChange } from "./map-panel-event-contract.js";

function finiteCoordinate(value) {
  if (
    value == null
    || typeof value === "boolean"
    || (typeof value === "string" && !value.trim())
  ) {
    return null;
  }
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function coordinateText(value) {
  return value == null ? mapText("info.location.unavailable") : String(value);
}

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>\"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] || char,
  );
}

function spriteIcon(name, sizeClass = "size-5") {
  return `<svg class="fishy-icon ${sizeClass}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${name}"></use></svg>`;
}

export function buildLocationBookmarkSection(selection) {
  if (selection?.detailsPending === true) {
    return null;
  }
  const coordinates = bookmarkCoordinatesFromSelection(selection);
  if (!coordinates) {
    return null;
  }
  const { worldX, worldY, worldZ } = coordinates;
  return {
    id: "location-bookmark",
    kind: "location-bookmark",
    title: mapText("info.location.title"),
    worldX,
    worldY,
    worldZ,
    canUseBookmark: worldY != null,
  };
}

export function buildLocationBookmarkAction(projection, options = {}) {
  const current = buildBookmarkPanelStateBundle({
    _map_ui: projection?.ui || {},
    _map_bookmarks: projection?.bookmarks || {},
    _map_runtime: projection?.runtime || {},
  });
  const bookmark = createBookmarkFromSelection(
    current.state.selection,
    current.storedBookmarks,
    {
      zoneCatalog: Array.isArray(options.zoneCatalog) ? options.zoneCatalog : [],
      runtimeLayers: current.state.catalog.layers,
    },
  );
  return bookmark ? { bookmark, current } : null;
}

export function createLocationBookmark(action, target) {
  if (!action?.bookmark || !action?.current) {
    return null;
  }
  const entries = normalizeBookmarks([
    ...action.current.storedBookmarks,
    action.bookmark,
  ]);
  const dispatched = dispatchBookmarksChange(target, entries, {
    placing: false,
    selectedIds: [action.bookmark.id],
  });
  return dispatched ? action.bookmark : null;
}

function coordinateMarkup(axis, value) {
  return `<div class="min-w-0 rounded-box border border-base-300/60 bg-base-100/75 px-2.5 py-2">
    <dt class="text-[10px] font-bold uppercase tracking-[0.16em] text-base-content/45">${axis}</dt>
    <dd class="m-0 mt-0.5 truncate font-mono text-sm font-semibold tabular-nums text-base-content">${escapeHtml(coordinateText(value))}</dd>
  </div>`;
}

export function locationBookmarkSectionMarkup(section) {
  const worldX = finiteCoordinate(section?.worldX);
  const worldY = finiteCoordinate(section?.worldY);
  const worldZ = finiteCoordinate(section?.worldZ);
  if (worldX == null || worldZ == null) {
    return "";
  }
  const canUseBookmark = section?.canUseBookmark === true && worldY != null;
  const copyLabel = mapText("info.location.copy_bookmark");
  const createLabel = mapText("info.location.create_bookmark");
  const unavailableReason = mapText("info.location.bookmark_unavailable");
  return `
    <section class="space-y-2">
      <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(section?.title || mapText("info.location.title"))}</p>
      <div class="rounded-box border border-base-300/70 bg-base-200/70 p-3">
        <p class="m-0 text-xs font-medium text-base-content/60">${escapeHtml(mapText("info.location.world_coordinates"))}</p>
        <dl class="m-0 mt-2 grid grid-cols-3 gap-2" aria-label="${escapeHtml(mapText("info.location.world_coordinates"))}">
          ${coordinateMarkup("X", worldX)}
          ${coordinateMarkup("Y", worldY)}
          ${coordinateMarkup("Z", worldZ)}
        </dl>
        <div class="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-primary btn-sm shrink-0"
            data-fishy-create-location-bookmark="true"
            aria-label="${escapeHtml(createLabel)}"
            data-fishy-tooltip="${escapeHtml(canUseBookmark ? createLabel : unavailableReason)}"
            ${canUseBookmark ? "" : "disabled"}
          >${spriteIcon("bookmark", "size-4")}<span>${escapeHtml(createLabel)}</span></button>
          <button
            type="button"
            class="btn btn-soft btn-sm shrink-0"
            data-fishy-copy-location-bookmark="true"
            aria-label="${escapeHtml(copyLabel)}"
            data-fishy-tooltip="${escapeHtml(canUseBookmark ? copyLabel : unavailableReason)}"
            ${canUseBookmark ? "" : "disabled"}
          >${spriteIcon("copy", "size-4")}<span>${escapeHtml(copyLabel)}</span></button>
        </div>
        ${
          canUseBookmark
            ? ""
            : `<p class="m-0 mt-2 text-xs text-warning">${escapeHtml(unavailableReason)}</p>`
        }
      </div>
    </section>
  `;
}

export async function copyLocationBookmark(bookmark, options = {}) {
  if (!bookmark) {
    throw new TypeError("location bookmark copy requires a finite XYZ coordinate");
  }
  const serialized = serializeBookmarkForCopy(bookmark);
  await copyTextToClipboard(serialized, options);
  return serialized;
}
